import { randomUUID } from "./uuid.js";
import { useEffect, useMemo, useRef, useState, type FormEvent } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api } from "../api.js";
import { ErrorState, Loading } from "../components/Layout.js";
import { useCountdown, useElapsedSeconds, useOnline } from "../hooks.js";
import type { Exercise, Workout, WorkoutSet } from "../types.js";
import { applyTargetRpeDefault } from "../../../shared/workout-rpe.js";
import { GymNative, isNativeAndroid, type NativePermissionStatus, type NativeTimerState } from "../native.js";
import { exerciseHistoryHref } from "../exercise-history.js";
import { findNextWorkoutSet } from "../workout-notification.js";
import {
  calculateRestoredScrollY,
  clearWorkoutResumePosition,
  readWorkoutResumePosition,
  writeWorkoutResumePosition
} from "../workout-resume.js";

const CACHE_KEY = "gym.activeWorkout.v1";
const REST_NOTIFICATIONS_KEY = "gym.restNotifications.v1";
const EMPTY_NATIVE_TIMER: NativeTimerState = {
  status: "idle",
  targetEpochMs: null,
  originalDurationSeconds: 0,
  workoutId: null,
  setId: null,
  exerciseName: null,
  workoutTitle: null,
  workoutActive: false,
  nextExerciseName: null,
  nextSetNumber: 0,
  nextSetDetails: null
};
const EMPTY_NATIVE_PERMISSIONS: NativePermissionStatus = { notifications: false, exactAlarms: false };
const RPE_LEVELS = [
  [5, "Very easy; at least five reps remaining"],
  [6, "Approximately four reps remaining"],
  [7, "Approximately three reps remaining"],
  [8, "Approximately two reps remaining"],
  [9, "Approximately one rep remaining"],
  [10, "Maximal effort; no further reps possible"]
] as const;

export function WorkoutPage() {
  const { id = "" } = useParams();
  const navigate = useNavigate();
  const online = useOnline();
  const [workout, setWorkout] = useState<Workout | null>(() => readCachedWorkoutFor(id));
  const [catalog, setCatalog] = useState<Exercise[]>([]);
  const [history, setHistory] = useState<Workout[]>([]);
  const [loading, setLoading] = useState(() => readCachedWorkoutFor(id) === null);
  const [error, setError] = useState<string | null>(null);
  const [showPicker, setShowPicker] = useState(false);
  const [showCustom, setShowCustom] = useState(false);
  const [showRpeGuide, setShowRpeGuide] = useState(false);
  const [showTerra, setShowTerra] = useState(false);
  const [constraints, setConstraints] = useState("");
  const [generating, setGenerating] = useState(false);
  const [search, setSearch] = useState("");
  const [restTarget, setRestTarget] = useState<number | null>(null);
  const [restSeconds, setRestSeconds] = useState(0);
  const [editingTiming, setEditingTiming] = useState(false);
  const [timingStart, setTimingStart] = useState("");
  const [timingFinish, setTimingFinish] = useState("");
  const [notificationEnabled, setNotificationEnabled] = useState(() => typeof Notification !== "undefined" && Notification.permission === "granted" && localStorage.getItem(REST_NOTIFICATIONS_KEY) === "enabled");
  const [nativeTimer, setNativeTimer] = useState<NativeTimerState>(EMPTY_NATIVE_TIMER);
  const [nativePermissions, setNativePermissions] = useState<NativePermissionStatus>(EMPTY_NATIVE_PERMISSIONS);
  const [syncState, setSyncState] = useState<"saved" | "saving" | "offline" | "unsaved">("saved");
  const saveTimer = useRef<number | null>(null);
  const localMutation = useRef(false);
  const loadedWorkoutId = useRef(id);
  const notifiedRestTarget = useRef<number | null>(null);
  const requestedNativeNotifications = useRef(false);
  const nativeTimerReady = isNativeAndroid && nativePermissions.notifications && nativePermissions.exactAlarms;
  const activeRestTarget = nativeTimerReady ? nativeTimer.targetEpochMs : restTarget;
  const activeRestSeconds = nativeTimerReady ? nativeTimer.originalDurationSeconds : restSeconds;
  const countdown = useCountdown(activeRestTarget, !nativeTimerReady);
  const elapsedSeconds = useElapsedSeconds(workout?.startedAt ?? null, workout?.completedAt ?? null);
  const historical = workout?.status === "completed";

  useEffect(() => {
    if (!isNativeAndroid) return;
    let disposed = false;
    let listener: { remove: () => Promise<void> } | undefined;
    const refresh = async () => {
      const [timer, permissions] = await Promise.all([GymNative.getRestTimerState(), GymNative.getPermissionStatus()]);
      if (!disposed) { setNativeTimer(timer); setNativePermissions(permissions); }
    };
    const visible = () => { if (document.visibilityState === "visible") void refresh(); };
    void refresh();
    void GymNative.addListener("restTimerChanged", (state) => { if (!disposed) setNativeTimer(state); }).then((handle) => { listener = handle; });
    document.addEventListener("visibilitychange", visible);
    return () => {
      disposed = true;
      document.removeEventListener("visibilitychange", visible);
      void listener?.remove();
    };
  }, []);

  useEffect(() => {
    if (!isNativeAndroid || workout?.status !== "in_progress" || nativePermissions.notifications || requestedNativeNotifications.current) return;
    requestedNativeNotifications.current = true;
    void GymNative.requestNotificationPermission().then(setNativePermissions).catch(() => undefined);
  }, [nativePermissions.notifications, workout?.status]);

  useEffect(() => {
    let disposed = false;
    const cached = readCachedWorkoutFor(id);
    if (loadedWorkoutId.current !== id) {
      loadedWorkoutId.current = id;
      localMutation.current = false;
      setWorkout(cached);
      setCatalog([]);
      setHistory([]);
      setLoading(cached === null);
      setError(null);
    }
    void Promise.allSettled([
      api.get<Workout>(`/api/workouts/${id}`),
      api.get<Exercise[]>("/api/exercises"),
      api.get<Workout[]>("/api/workouts?limit=100&status=completed")
    ]).then(([workoutResult, exerciseResult, historyResult]) => {
      if (disposed) return;
      if (workoutResult.status === "fulfilled") {
        setWorkout((current) => chooseFreshestWorkout(workoutResult.value, current?.id === id ? current : cached, id));
      }
      if (exerciseResult.status === "fulfilled") setCatalog(exerciseResult.value);
      if (historyResult.status === "fulfilled") setHistory(historyResult.value.filter((past) => past.id !== id));
      const failed = [workoutResult, exerciseResult, historyResult].filter((result) => result.status === "rejected");
      if (workoutResult.status === "rejected" && !cached) {
        const cause = workoutResult.reason as unknown;
        setError(cause instanceof Error ? cause.message : "Unable to load workout");
      } else if (failed.length) {
        setError("Using saved workout data. Some online details could not refresh.");
        if (workoutResult.status === "rejected") setSyncState("offline");
      } else {
        setError(null);
      }
      setLoading(false);
    });
    return () => { disposed = true; };
  }, [id]);

  useEffect(() => {
    if (!workout || workout.status === "completed") return;
    localStorage.setItem(CACHE_KEY, JSON.stringify(workout));
    if (!localMutation.current) return;
    if (saveTimer.current) window.clearTimeout(saveTimer.current);
    if (!online) { setSyncState("offline"); return; }
    setSyncState("saving");
    saveTimer.current = window.setTimeout(() => {
      saveTimer.current = null;
      localMutation.current = false;
      void api.put<Workout>(`/api/workouts/${workout.id}`, workout)
        .then(() => setSyncState("saved"))
        .catch(() => {
          localMutation.current = true;
          setSyncState("offline");
        });
    }, 650);
    return () => { if (saveTimer.current) window.clearTimeout(saveTimer.current); };
  }, [workout, online]);

  useEffect(() => {
    if (!isNativeAndroid || !nativePermissions.notifications || workout?.status !== "in_progress") return;
    const next = findNextWorkoutSet(workout);
    void GymNative.syncActiveWorkout({
      workoutId: workout.id,
      workoutTitle: workout.title,
      nextExerciseName: next?.exerciseName ?? null,
      nextSetNumber: next?.setNumber ?? 0,
      nextSetDetails: next?.details ?? null
    }).then(setNativeTimer).catch(() => undefined);
  }, [nativePermissions.notifications, workout]);

  useEffect(() => {
    if (!workout) return;
    if (workout.status !== "in_progress") {
      clearWorkoutResumePosition(localStorage, workout.id);
      return;
    }
    const previousScrollRestoration = window.history.scrollRestoration;
    window.history.scrollRestoration = "manual";
    let positionTimer: number | null = null;
    let firstFrame = 0;
    let secondFrame = 0;

    const capture = () => {
      if (positionTimer !== null) {
        window.clearTimeout(positionTimer);
        positionTimer = null;
      }
      const cards = [...document.querySelectorAll<HTMLElement>("[data-workout-exercise-id]")];
      const visibleCards = cards
        .map((element) => ({ element, rect: element.getBoundingClientRect() }))
        .filter(({ rect }) => rect.bottom > 0 && rect.top < window.innerHeight);
      const focusY = Math.min(160, window.innerHeight / 4);
      const anchor = visibleCards.find(({ rect }) => rect.top <= focusY && rect.bottom > focusY)
        ?? visibleCards.find(({ rect }) => rect.top >= 0)
        ?? visibleCards[0]
        ?? null;
      writeWorkoutResumePosition(localStorage, {
        version: 1,
        workoutId: workout.id,
        exerciseId: anchor?.element.dataset.workoutExerciseId ?? null,
        exerciseOffsetPx: anchor?.rect.top ?? null,
        scrollY: Math.max(0, window.scrollY),
        savedAt: Date.now()
      });
    };
    const scheduleCapture = () => {
      if (positionTimer !== null) return;
      positionTimer = window.setTimeout(capture, 200);
    };
    const captureWhenHidden = () => {
      if (document.visibilityState === "hidden") capture();
    };

    const saved = readWorkoutResumePosition(localStorage, workout.id);
    if (saved) {
      firstFrame = window.requestAnimationFrame(() => {
        secondFrame = window.requestAnimationFrame(() => {
          const anchor = saved.exerciseId
            ? [...document.querySelectorAll<HTMLElement>("[data-workout-exercise-id]")]
              .find((element) => element.dataset.workoutExerciseId === saved.exerciseId)
            : null;
          const maximumScrollY = Math.max(0, document.documentElement.scrollHeight - window.innerHeight);
          const target = calculateRestoredScrollY(
            saved,
            window.scrollY,
            anchor?.getBoundingClientRect().top ?? null,
            maximumScrollY
          );
          window.scrollTo(0, target);
        });
      });
    }

    window.addEventListener("scroll", scheduleCapture, { passive: true });
    window.addEventListener("pagehide", capture);
    document.addEventListener("visibilitychange", captureWhenHidden);
    return () => {
      window.cancelAnimationFrame(firstFrame);
      window.cancelAnimationFrame(secondFrame);
      if (positionTimer !== null) window.clearTimeout(positionTimer);
      window.removeEventListener("scroll", scheduleCapture);
      window.removeEventListener("pagehide", capture);
      document.removeEventListener("visibilitychange", captureWhenHidden);
      window.history.scrollRestoration = previousScrollRestoration;
    };
  }, [workout?.id, workout?.status]);

  useEffect(() => {
    if (workout?.status !== "in_progress") return;
    let sentinel: WakeLockSentinel | null = null;
    const acquire = async () => {
      if ("wakeLock" in navigator && document.visibilityState === "visible") {
        try { sentinel = await navigator.wakeLock.request("screen"); } catch { /* device may refuse */ }
      }
    };
    const visible = () => { if (document.visibilityState === "visible" && !sentinel) void acquire(); };
    void acquire(); document.addEventListener("visibilitychange", visible);
    return () => { document.removeEventListener("visibilitychange", visible); void sentinel?.release(); };
  }, [workout?.status]);

  useEffect(() => {
    if (isNativeAndroid || !workout || !restTarget || countdown > 0 || Date.now() < restTarget || notifiedRestTarget.current === restTarget) return;
    notifiedRestTarget.current = restTarget;
    if (!notificationEnabled || typeof Notification === "undefined" || Notification.permission !== "granted" || !("serviceWorker" in navigator)) return;
    void navigator.serviceWorker.ready.then((registration) => registration.showNotification("Rest complete", {
      body: `${workout.title} is ready for the next set.`,
      icon: "/gym-icon.svg",
      badge: "/gym-icon.svg",
      tag: `gym-rest-${workout.id}`,
      vibrate: [250, 120, 250],
      data: { url: `/workout/${workout.id}` }
    } as NotificationOptions & { vibrate: number[] })).catch(() => undefined);
  }, [countdown, notificationEnabled, restTarget, workout?.id, workout?.title]);

  const previousByExercise = useMemo(() => {
    const result = new Map<string, Workout["exercises"][number]>();
    for (const past of history) for (const exercise of past.exercises) if (!result.has(exercise.name)) result.set(exercise.name, exercise);
    return result;
  }, [history]);

  if (loading && !workout) return <Loading />;
  if (!workout) return <ErrorState message={error ?? "Workout not found"} />;

  const mutate = (recipe: (draft: Workout) => void) => {
    if (!historical) localMutation.current = true;
    setWorkout((current) => {
      if (!current) return current;
      const draft = structuredClone(current);
      recipe(draft);
      draft.updatedAt = new Date().toISOString();
      draft.treadmillMinutes = draft.exercises.some(isTreadmill) ? 10 : 0;
      draft.estimatedMinutes = draft.exercises.reduce((sum, exercise) => sum + exercise.estimatedMinutes, 0);
      return draft;
    });
    if (historical) setSyncState("unsaved");
  };

  const toggleSet = (exerciseIndex: number, setIndex: number) => {
    const exercise = workout.exercises[exerciseIndex];
    const set = exercise?.sets[setIndex];
    if (!exercise || !set) return;
    const completing = !set.completed;
    mutate((draft) => {
      const target = draft.exercises[exerciseIndex]?.sets[setIndex];
      if (!target) return;
      target.completed = completing;
      target.completedAt = completing && draft.status !== "completed" ? new Date().toISOString() : null;
      applyTargetRpeDefault(target);
    });
    if (completing && workout.status === "in_progress" && !isTreadmill(exercise)) {
      navigator.vibrate?.(40);
      if (nativeTimerReady) {
        const nextWorkout = structuredClone(workout);
        const completedSet = nextWorkout.exercises[exerciseIndex]?.sets[setIndex];
        if (completedSet) completedSet.completed = true;
        const next = findNextWorkoutSet(nextWorkout);
        void GymNative.startRestTimer({
          durationSeconds: exercise.restSeconds,
          workoutId: workout.id,
          setId: set.id,
          exerciseName: exercise.name,
          workoutTitle: workout.title,
          setNumber: setIndex + 1,
          nextExerciseName: next?.exerciseName ?? null,
          nextSetNumber: next?.setNumber ?? 0,
          nextSetDetails: next?.details ?? null
        }).then(setNativeTimer).catch(() => {
          setRestSeconds(exercise.restSeconds);
          setRestTarget(Date.now() + exercise.restSeconds * 1000);
        });
      } else {
        setRestSeconds(exercise.restSeconds);
        setRestTarget(Date.now() + exercise.restSeconds * 1000);
      }
    } else if (!completing && nativeTimerReady && nativeTimer.setId === set.id) {
      void GymNative.cancelRestTimer().then(setNativeTimer);
    }
  };
  const updateSet = (exerciseIndex: number, setIndex: number, field: keyof WorkoutSet, value: unknown) => mutate((draft) => {
    const set = draft.exercises[exerciseIndex]?.sets[setIndex]; if (set) Reflect.set(set, field, value);
  });
  const addSet = (exerciseIndex: number) => mutate((draft) => {
    const exercise = draft.exercises[exerciseIndex]; if (!exercise) return;
    const previous = exercise.sets.at(-1);
    exercise.sets.push(previous ? { ...structuredClone(previous), id: randomUUID(), targetReps: null, completed: false, completedAt: null, rpe: null } : emptySet(exercise.measurementType));
    exercise.estimatedMinutes += Math.max(1, Math.round(exercise.restSeconds / 60));
  });
  const removeSet = (exerciseIndex: number, setIndex: number) => {
    const removedId = workout.exercises[exerciseIndex]?.sets[setIndex]?.id;
    if (nativeTimerReady && removedId === nativeTimer.setId) void GymNative.cancelRestTimer().then(setNativeTimer);
    mutate((draft) => {
      const sets = draft.exercises[exerciseIndex]?.sets; if (sets && sets.length > 1) sets.splice(setIndex, 1);
    });
  };
  const removeExercise = (exerciseIndex: number) => {
    const exercise = workout.exercises[exerciseIndex];
    if (!exercise || !window.confirm(`Remove ${exercise.name}?`)) return;
    if (nativeTimerReady && exercise.sets.some((set) => set.id === nativeTimer.setId)) void GymNative.cancelRestTimer().then(setNativeTimer);
    mutate((draft) => draft.exercises.splice(exerciseIndex, 1));
  };
  const addExercise = (exercise: Exercise) => {
    mutate((draft) => draft.exercises.push(toWorkoutExercise(exercise)));
    setShowPicker(false); setShowCustom(false); setSearch("");
  };
  const finish = async () => {
    if (!window.confirm("Finish this workout?")) return;
    try {
      const saved = await api.put<Workout>(`/api/workouts/${workout.id}`, workout);
      await api.post(`/api/workouts/${saved.id}/finish`);
      if (isNativeAndroid) await GymNative.clearActiveWorkout().catch(() => undefined);
      clearCachedWorkout(workout.id); navigate("/history");
    } catch (cause) { setError(cause instanceof Error ? cause.message : "Could not finish workout"); }
  };
  const saveHistorical = async () => {
    if (!online) { setError("Connect to the internet before saving historical changes."); return; }
    try {
      const saved = await api.put<Workout>(`/api/workouts/${workout.id}`, workout);
      setWorkout(saved); setSyncState("saved");
    } catch (cause) { setError(cause instanceof Error ? cause.message : "Could not save workout"); }
  };
  const openTimingEditor = () => {
    if (!workout.startedAt) return;
    setTimingStart(toLocalDateTime(workout.startedAt));
    setTimingFinish(workout.completedAt ? toLocalDateTime(workout.completedAt) : "");
    setEditingTiming(true);
  };
  const saveTiming = async () => {
    if (!timingStart || (historical && !timingFinish)) return;
    try {
      const saved = await api.patch<Workout>(`/api/workouts/${workout.id}/timing`, {
        startedAt: new Date(timingStart).toISOString(),
        completedAt: historical ? new Date(timingFinish).toISOString() : null
      });
      setWorkout(saved); setEditingTiming(false); setSyncState("saved");
    } catch (cause) { setError(cause instanceof Error ? cause.message : "Could not update workout timing"); }
  };
  const enableRestNotifications = async () => {
    if (typeof Notification === "undefined" || !("serviceWorker" in navigator)) return;
    if (notificationEnabled) {
      setNotificationEnabled(false);
      localStorage.removeItem(REST_NOTIFICATIONS_KEY);
      return;
    }
    const permission = await Notification.requestPermission();
    const enabled = permission === "granted";
    setNotificationEnabled(enabled);
    if (enabled) localStorage.setItem(REST_NOTIFICATIONS_KEY, "enabled");
    else localStorage.removeItem(REST_NOTIFICATIONS_KEY);
  };
  const setupNativeRestTimer = async () => {
    const afterNotifications = await GymNative.requestNotificationPermission();
    setNativePermissions(afterNotifications);
    if (!afterNotifications.exactAlarms) await GymNative.openExactAlarmSettings();
  };
  const adjustRestTimer = (deltaSeconds: number) => {
    if (nativeTimerReady && nativeTimer.status !== "idle") void GymNative.adjustRestTimer({ deltaSeconds }).then(setNativeTimer);
    else setRestTarget(Date.now() + Math.max(0, countdown + deltaSeconds) * 1000);
  };
  const cancelRestTimer = () => {
    setRestTarget(null);
    if (nativeTimerReady && nativeTimer.status !== "idle") void GymNative.cancelRestTimer().then(setNativeTimer);
  };
  const removeWorkout = async (kind: "cancel" | "delete") => {
    if (!online) { setError(`Connect to the internet before you ${kind} this workout.`); return; }
    const date = new Date(workout.date).toLocaleDateString("en-AU", { day: "numeric", month: "short", year: "numeric" });
    const warning = kind === "cancel"
      ? `Cancel and permanently delete “${workout.title}” (${date})? All sets, RPE and notes will be removed.`
      : `Permanently delete “${workout.title}” (${date})? This cannot be undone.`;
    if (!window.confirm(warning)) return;
    try {
      await api.delete(`/api/workouts/${workout.id}`);
      if (isNativeAndroid) await GymNative.clearActiveWorkout().catch(() => undefined);
      clearCachedWorkout(workout.id);
      navigate(kind === "cancel" ? "/" : "/history");
    } catch (cause) { setError(cause instanceof Error ? cause.message : "Could not delete workout"); }
  };
  const appendWithTerra = async (event: FormEvent) => {
    event.preventDefault();
    if (!online) { setError("Connect to the internet before using Terra."); return; }
    if (!window.confirm("Add exercises with Terra? This uses separately billed OpenAI API credits.")) return;
    setGenerating(true); setError(null);
    try {
      await api.put(`/api/workouts/${workout.id}`, workout);
      const updated = await api.post<Workout>(`/api/workouts/${workout.id}/ai/append`, { constraints });
      if (updated.exercises.length === workout.exercises.length) window.alert("Terra did not find useful additions that fit the remaining session.");
      setWorkout(updated); setConstraints(""); setShowTerra(false); setSyncState("saved");
    } catch (cause) { setError(cause instanceof Error ? cause.message : "Terra could not add exercises"); }
    finally { setGenerating(false); }
  };

  const completed = workout.exercises.reduce((sum, exercise) => sum + exercise.sets.filter((set) => set.completed).length, 0);
  const total = workout.exercises.reduce((sum, exercise) => sum + exercise.sets.length, 0);
  const canUseTerra = workout.source === "manual" && workout.status === "in_progress";

  return <div className={`workout-page ${historical ? "historical-workout" : ""}`}>
    <div className="workout-toolbar">
      <button className="icon-button" onClick={() => navigate(historical ? "/history" : "/")}>←</button>
      <div><strong>{workout.title}</strong><small>{historical ? (syncState === "unsaved" ? "Unsaved changes" : "Historical workout") : syncLabel(syncState)}</small></div>
      {historical
        ? <button className="finish-button" disabled={syncState !== "unsaved"} onClick={() => void saveHistorical()}>Save</button>
        : <button className="finish-button" onClick={() => void finish()}>Finish</button>}
    </div>

    {!historical && activeRestTarget && <div className={`rest-banner ${countdown === 0 ? "done" : ""}`}>
      <div><span>{countdown === 0 ? "Rest complete" : "Rest timer"}</span><strong>{formatTimer(countdown)}</strong></div>
      <div className="rest-actions"><button onClick={() => adjustRestTimer(-15)}>−15</button><button onClick={() => adjustRestTimer(15)}>+15</button><button onClick={cancelRestTimer}>×</button></div>
      <div className="rest-progress" style={{ width: `${activeRestSeconds ? Math.min(100, countdown / activeRestSeconds * 100) : 0}%` }} />
    </div>}

    {historical && <section className="workout-meta-card">
      <label>Workout title<input value={workout.title} onChange={(event) => mutate((draft) => { draft.title = event.target.value; })} /></label>
      <div className="field-pair"><label>Date and time<input type="datetime-local" value={toLocalDateTime(workout.date)} onChange={(event) => { if (event.target.value) mutate((draft) => { draft.date = new Date(event.target.value).toISOString(); }); }} /></label><label>Location<select value={workout.location} onChange={(event) => mutate((draft) => { draft.location = event.target.value as Workout["location"]; })}><option value="gym">Commercial gym</option><option value="home">Home</option></select></label></div>
      <label>Workout notes<textarea rows={2} value={workout.notes} onChange={(event) => mutate((draft) => { draft.notes = event.target.value; })} /></label>
      <div className="provenance-line"><span>Source: {sourceLabel(workout)}</span></div>
    </section>}

    {error && <div className="form-error workout-error">{error}<button onClick={() => setError(null)}>×</button></div>}
    {workout.startedAt && <section className="session-timing">
      <button className="session-time-button" onClick={openTimingEditor}><span>Started</span><strong>{formatClock(workout.startedAt)}</strong><small>Tap to edit</small></button>
      <div><span>Elapsed</span><strong>{formatElapsed(elapsedSeconds)}</strong></div>
      {!historical && isNativeAndroid && <button className={`notification-button ${nativePermissions.notifications ? "enabled" : ""}`} onClick={() => void setupNativeRestTimer()}>{nativeTimerReady ? "Workout notifications on" : nativePermissions.notifications ? "Notifications on · enable exact timers" : "Enable workout notifications"}</button>}
      {!historical && !isNativeAndroid && typeof Notification !== "undefined" && <button className={`notification-button ${notificationEnabled ? "enabled" : ""}`} onClick={() => void enableRestNotifications()}>{notificationEnabled ? "Notifications on" : "Enable rest notifications"}</button>}
    </section>}
    {editingTiming && <section className="timing-editor">
      <label>Started<input type="datetime-local" value={timingStart} onChange={(event) => setTimingStart(event.target.value)} /></label>
      {historical && <label>Finished<input type="datetime-local" value={timingFinish} onChange={(event) => setTimingFinish(event.target.value)} /></label>}
      <div><button className="text-button" onClick={() => setEditingTiming(false)}>Cancel</button><button className="primary-button" onClick={() => void saveTiming()}>Save timing</button></div>
    </section>}
    <div className="workout-summary"><div><span>Progress</span><strong>{completed}/{total} sets</strong></div><div><span>Estimate</span><strong>{workout.estimatedMinutes} min</strong></div><div><span>Location</span><strong>{workout.location}</strong></div></div>

    {!workout.exercises.length && <div className="empty-workout"><strong>Blank workout</strong><p>Add exercises yourself or ask Terra to build around today’s training.</p></div>}
    <div className="exercise-stack">
      {workout.exercises.map((exercise, exerciseIndex) => {
        const previous = previousByExercise.get(exercise.name);
        const finalWorkingIndex = exercise.sets.findLastIndex((set) => set.type !== "warmup");
        const treadmill = isTreadmill(exercise);
        return <section className={`exercise-card ${treadmill ? "treadmill-exercise" : ""}`} data-workout-exercise-id={exercise.id} key={exercise.id}>
          <div className="exercise-header"><div><span className="exercise-number">{exerciseIndex + 1}</span><h2><button className="exercise-history-link" onClick={() => navigate(exerciseHistoryHref(exercise))}>{exercise.name}<span aria-hidden="true">›</span></button></h2><small>{exercise.primaryMuscles.map(pretty).join(" · ")}</small></div><div className="exercise-actions">
            <button disabled={exerciseIndex === 0} onClick={() => mutate((draft) => move(draft.exercises, exerciseIndex, exerciseIndex - 1))}>↑</button>
            <button disabled={exerciseIndex === workout.exercises.length - 1} onClick={() => mutate((draft) => move(draft.exercises, exerciseIndex, exerciseIndex + 1))}>↓</button>
            <button onClick={() => removeExercise(exerciseIndex)}>×</button>
          </div></div>
          {exercise.optional && <div className="optional-pill">Optional if short on time</div>}
          {previous && <div className="previous-line">Previous: {previous.sets.filter((set) => set.type !== "warmup").slice(0, 3).map(formatSet).join(" · ")}</div>}
          <div className="set-grid set-grid-header"><span>Set</span><span>{exercise.measurementType === "assisted_reps" ? "Assist" : exercise.measurementType === "duration_power" ? "Minutes" : exercise.measurementType === "duration" ? "Seconds" : exercise.measurementType === "distance" ? "Metres" : "kg"}</span><span>{exercise.measurementType === "duration_power" ? "Avg W" : treadmill ? "Metres" : "Reps"}</span><button className="rpe-help" onClick={() => setShowRpeGuide(true)}>RPE ?</button><span>Done</span><span /></div>
          {exercise.sets.map((set, setIndex) => <div className={`set-grid ${set.completed ? "set-complete" : ""}`} key={set.id}>
            <button className={`set-type ${set.type}`} onClick={() => updateSet(exerciseIndex, setIndex, "type", cycleSetType(set.type))}>{set.type === "warmup" ? "W" : setIndex + 1}</button>
            {exercise.measurementType === "duration_power" ? <MinutesInput valueSeconds={set.seconds} onChange={(value) => updateSet(exerciseIndex, setIndex, "seconds", value)} /> : exercise.measurementType === "duration" ? <NumberInput value={set.seconds} onChange={(value) => updateSet(exerciseIndex, setIndex, "seconds", value)} /> : exercise.measurementType === "distance" ? <NumberInput value={set.distanceM} onChange={(value) => updateSet(exerciseIndex, setIndex, "distanceM", value)} /> : exercise.measurementType === "assisted_reps" ? <NumberInput value={set.assistanceKg} onChange={(value) => updateSet(exerciseIndex, setIndex, "assistanceKg", value)} /> : <WeightInput value={set.weightKg} options={weightOptionsFor(exercise, catalog)} onChange={(value) => updateSet(exerciseIndex, setIndex, "weightKg", value)} />}
            {exercise.measurementType === "duration_power"
              ? <NumberInput value={set.averageWatts ?? null} onChange={(value) => updateSet(exerciseIndex, setIndex, "averageWatts", value)} />
              : treadmill
              ? <NumberInput value={set.distanceM} onChange={(value) => updateSet(exerciseIndex, setIndex, "distanceM", value)} />
              : <div className="reps-field"><NumberInput value={set.reps} disabled={exercise.measurementType === "duration" || exercise.measurementType === "distance"} onChange={(value) => updateSet(exerciseIndex, setIndex, "reps", value)} />{set.targetReps !== null && set.targetReps !== undefined && <small>Target {set.targetReps}</small>}</div>}
            <select disabled={set.type === "warmup" || treadmill} className={setIndex === finalWorkingIndex ? "final-rpe" : ""} aria-label={setIndex === finalWorkingIndex ? "RPE for final working set" : "RPE"} value={set.rpe ?? ""} onChange={(event) => updateSet(exerciseIndex, setIndex, "rpe", event.target.value ? Number(event.target.value) : null)}>
              <option value="">{set.targetRpe ? `T${set.targetRpe}` : "—"}</option>{RPE_LEVELS.map(([value, description]) => <option key={value} value={value}>{value} · {description}</option>)}
            </select>
            <button aria-label="Complete set" className="check-button" onClick={() => toggleSet(exerciseIndex, setIndex)}>{set.completed ? "✓" : ""}</button>
            <button className="remove-set" disabled={exercise.sets.length === 1} onClick={() => removeSet(exerciseIndex, setIndex)}>−</button>
          </div>)}
          <button className="add-set-button" onClick={() => addSet(exerciseIndex)}>+ Add set</button>
          <label className="notes-label">Exercise note<AutoTextarea value={exercise.notes} onChange={(value) => mutate((draft) => { const item = draft.exercises[exerciseIndex]; if (item) item.notes = value; })} /></label>
        </section>;
      })}
    </div>

    <button className="add-exercise-button" onClick={() => setShowPicker(true)}>+ Add exercise</button>
    {canUseTerra && <button className="terra-button" onClick={() => setShowTerra(true)}>Add exercises with Terra</button>}
    {historical ? <div className="historical-actions"><button className="primary-button" disabled={syncState !== "unsaved"} onClick={() => void saveHistorical()}>Save changes</button><button className="danger-button" onClick={() => void removeWorkout("delete")}>Delete workout</button></div>
      : <><div className="finish-panel"><div><span>{completed} of {total} sets</span><strong>{total ? Math.round(completed / total * 100) : 0}% complete</strong></div><button className="primary-button" onClick={() => void finish()}>Finish workout</button></div><button className="danger-text-button" onClick={() => void removeWorkout("cancel")}>Cancel workout</button></>}

    {showPicker && <ExercisePicker catalog={catalog} location={workout.location} search={search} setSearch={setSearch} showCustom={showCustom} setShowCustom={setShowCustom} onAdd={addExercise} onCreated={(exercise) => { setCatalog((items) => [...items, exercise].sort((a, b) => a.name.localeCompare(b.name))); addExercise(exercise); }} onClose={() => { setShowPicker(false); setShowCustom(false); }} />}
    {showRpeGuide && <RpeGuide onClose={() => setShowRpeGuide(false)} />}
    {showTerra && <div className="modal-backdrop" onClick={() => !generating && setShowTerra(false)}><section className="picker-sheet compact-sheet" onClick={(event) => event.stopPropagation()}><div className="picker-handle" /><div className="picker-title"><h2>Add with Terra</h2><button disabled={generating} onClick={() => setShowTerra(false)}>×</button></div><p>Terra will preserve this workout and append exercises that fit the remaining time and today’s completed work.</p><form onSubmit={(event) => void appendWithTerra(event)}><label>Optional constraints<textarea rows={3} placeholder="For example: avoid overhead pressing" value={constraints} onChange={(event) => setConstraints(event.target.value)} /></label><button className="primary-button full" disabled={generating}>{generating ? "Generating…" : "Generate additions"}</button></form></section></div>}
  </div>;
}

function ExercisePicker({ catalog, location, search, setSearch, showCustom, setShowCustom, onAdd, onCreated, onClose }: { catalog: Exercise[]; location: Workout["location"]; search: string; setSearch: (value: string) => void; showCustom: boolean; setShowCustom: (value: boolean) => void; onAdd: (exercise: Exercise) => void; onCreated: (exercise: Exercise) => void; onClose: () => void }) {
  const matches = catalog.filter((exercise) => `${exercise.name} ${exercise.aliases.join(" ")}`.toLowerCase().includes(search.toLowerCase()) && exercise.locations.includes(location) && exercise.preference !== "excluded");
  return <div className="modal-backdrop" onClick={onClose}><section className="picker-sheet" onClick={(event) => event.stopPropagation()}><div className="picker-handle" /><div className="picker-title"><h2>{showCustom ? "Create exercise" : "Add exercise"}</h2><button onClick={onClose}>×</button></div>{showCustom ? <CustomExerciseForm location={location} onCreated={onCreated} onBack={() => setShowCustom(false)} /> : <><input className="search-input" placeholder="Search names and aliases" autoFocus value={search} onChange={(event) => setSearch(event.target.value)} /><button className="create-exercise-button" onClick={() => setShowCustom(true)}>+ Create custom exercise</button><div className="picker-list">{matches.map((exercise) => <button key={exercise.id} onClick={() => onAdd(exercise)}><span><strong>{exercise.name}</strong><small>{exercise.primaryMuscles.map(pretty).join(" · ")}</small></span><b>+</b></button>)}</div></>}</section></div>;
}

function CustomExerciseForm({ location, onCreated, onBack }: { location: Workout["location"]; onCreated: (exercise: Exercise) => void; onBack: () => void }) {
  const [name, setName] = useState("");
  const [measurementType, setMeasurementType] = useState<Exercise["measurementType"]>("weight_reps");
  const [muscles, setMuscles] = useState("");
  const [equipment, setEquipment] = useState("");
  const [rest, setRest] = useState(90);
  const [bothLocations, setBothLocations] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const submit = async (event: FormEvent) => {
    event.preventDefault(); setError(null);
    try {
      const created = await api.post<Exercise>("/api/exercises", {
        name, measurementType, primaryMuscles: splitList(muscles), secondaryMuscles: [], equipment: splitList(equipment),
        locations: bothLocations ? ["gym", "home"] : [location], defaultRestSeconds: rest, aliases: []
      });
      onCreated(created);
    } catch (cause) { setError(cause instanceof Error ? cause.message : "Could not create exercise"); }
  };
  return <form className="custom-exercise-form" onSubmit={(event) => void submit(event)}>{error && <div className="form-error">{error}</div>}<label>Name<input required value={name} onChange={(event) => setName(event.target.value)} /></label><label>Tracking<select value={measurementType} onChange={(event) => setMeasurementType(event.target.value as Exercise["measurementType"])}><option value="weight_reps">Weight + reps</option><option value="bodyweight_reps">Bodyweight + reps</option><option value="assisted_reps">Assistance + reps</option><option value="duration">Duration</option><option value="distance">Distance</option><option value="duration_power">Time + average watts</option></select></label><label>Primary muscles<input required placeholder="chest, triceps" value={muscles} onChange={(event) => setMuscles(event.target.value)} /></label><label>Equipment<input placeholder="cable, bench" value={equipment} onChange={(event) => setEquipment(event.target.value)} /></label><label>Default rest (seconds)<input type="number" min={15} max={600} value={rest} onChange={(event) => setRest(Number(event.target.value))} /></label><label className="checkbox-label"><input type="checkbox" checked={bothLocations} onChange={(event) => setBothLocations(event.target.checked)} />Available at gym and home</label><div className="form-actions"><button type="button" className="text-button" onClick={onBack}>Back</button><button className="primary-button">Create and add</button></div></form>;
}

function RpeGuide({ onClose }: { onClose: () => void }) {
  return <div className="modal-backdrop" onClick={onClose}><section className="picker-sheet compact-sheet" onClick={(event) => event.stopPropagation()}><div className="picker-handle" /><div className="picker-title"><h2>RPE guidance</h2><button onClick={onClose}>×</button></div><p>Rate the effort of the completed set. The final set is emphasized.</p><div className="rpe-guide-list">{RPE_LEVELS.map(([level, meaning]) => <div key={level}><strong>{level}</strong><span>{meaning}</span></div>)}</div><p><strong>T8</strong> means the suggested target is RPE 8. Complete the set without selecting another value to record 8, or select a number to record your actual RPE.</p></section></div>;
}

function NumberInput({ value, onChange, disabled = false }: { value: number | null; onChange: (value: number | null) => void; disabled?: boolean }) { return <input disabled={disabled} inputMode="decimal" type="number" step="any" value={value ?? ""} onChange={(event) => onChange(event.target.value === "" ? null : Number(event.target.value))} />; }
function MinutesInput({ valueSeconds, onChange }: { valueSeconds: number | null; onChange: (value: number | null) => void }) { return <input inputMode="decimal" type="number" min="0" step="any" value={valueSeconds === null ? "" : valueSeconds / 60} onChange={(event) => onChange(event.target.value === "" ? null : Number(event.target.value) * 60)} />; }
function WeightInput({ value, options, onChange }: { value: number | null; options: number[]; onChange: (value: number | null) => void }) {
  if (!options.length) return <NumberInput value={value} onChange={onChange} />;
  const custom = value !== null && !options.includes(value);
  return <select aria-label="Weight" value={value ?? ""} onChange={(event) => onChange(event.target.value === "" ? null : Number(event.target.value))}>
    <option value="">—</option>
    {custom && <option value={value!}>{value} kg (custom)</option>}
    {options.map((option) => <option key={option} value={option}>{option} kg</option>)}
  </select>;
}
function AutoTextarea({ value, onChange }: { value: string; onChange: (value: string) => void }) {
  const ref = useRef<HTMLTextAreaElement>(null);
  useEffect(() => {
    const element = ref.current;
    if (!element) return;
    element.style.height = "auto";
    element.style.height = `${element.scrollHeight}px`;
  }, [value]);
  return <textarea ref={ref} rows={2} value={value} onChange={(event) => onChange(event.target.value)} />;
}
function emptySet(type: Exercise["measurementType"]): WorkoutSet { return { id: randomUUID(), type: "working", weightKg: type === "bodyweight_reps" ? 0 : null, assistanceKg: type === "assisted_reps" ? 30 : null, reps: type === "duration" || type === "distance" || type === "duration_power" ? null : 8, targetReps: null, seconds: type === "duration" ? 30 : type === "duration_power" ? 600 : null, distanceM: type === "distance" ? 100 : null, averageWatts: null, targetRpe: type === "duration" || type === "distance" || type === "duration_power" ? null : 7, rpe: null, completed: false, completedAt: null, notes: "" }; }
function toWorkoutExercise(exercise: Exercise): Workout["exercises"][number] { const setCount = exercise.measurementType === "duration_power" ? 1 : 3; return { id: randomUUID(), exerciseId: exercise.id, name: exercise.name, measurementType: exercise.measurementType, primaryMuscles: exercise.primaryMuscles, equipment: exercise.equipment, restSeconds: exercise.defaultRestSeconds, estimatedMinutes: Math.round(2 + (exercise.learnedMinutesPerSet ?? 3) * setCount), notes: "", optional: false, locked: false, sets: Array.from({ length: setCount }, () => emptySet(exercise.measurementType)) }; }
function cycleSetType(current: WorkoutSet["type"]): WorkoutSet["type"] { return current === "working" ? "warmup" : current === "warmup" ? "failure" : "working"; }
function formatTimer(seconds: number) { return `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, "0")}`; }
function formatElapsed(seconds: number) { const hours = Math.floor(seconds / 3600); const minutes = Math.floor(seconds % 3600 / 60); const rest = seconds % 60; return hours ? `${hours}:${String(minutes).padStart(2, "0")}:${String(rest).padStart(2, "0")}` : `${minutes}:${String(rest).padStart(2, "0")}`; }
function formatClock(iso: string) { return new Date(iso).toLocaleTimeString("en-AU", { hour: "numeric", minute: "2-digit" }); }
function pretty(value: string) { return value.replaceAll("_", " ").replace(/\b\w/g, (letter) => letter.toUpperCase()); }
function formatSet(set: WorkoutSet) { if (set.averageWatts !== null && set.averageWatts !== undefined) return `${formatMinutes(set.seconds)} @ ${set.averageWatts}W`; if (set.seconds) return `${set.seconds}s`; if (set.distanceM) return `${set.distanceM}m`; const weight = set.assistanceKg !== null ? `−${set.assistanceKg}kg` : set.weightKg ? `+${set.weightKg}kg` : "BW"; return `${weight}×${set.reps ?? "—"}`; }
function formatMinutes(seconds: number | null) { if (seconds === null) return "—"; const minutes = seconds / 60; return `${Number.isInteger(minutes) ? minutes : minutes.toFixed(1)} min`; }
function move<T>(items: T[], from: number, to: number) { const [item] = items.splice(from, 1); if (item !== undefined) items.splice(to, 0, item); }
function readCachedWorkout(): Workout | null { try { const value = localStorage.getItem(CACHE_KEY); return value ? JSON.parse(value) as Workout : null; } catch { return null; } }
function readCachedWorkoutFor(id: string): Workout | null {
  const cached = readCachedWorkout();
  return cached?.id === id && cached.status === "in_progress" ? cached : null;
}
function chooseFreshestWorkout(server: Workout, cached: Workout | null, id: string): Workout {
  const useCache = server.status === "in_progress"
    && cached?.id === id
    && Date.parse(cached.updatedAt) >= Date.parse(server.updatedAt);
  return useCache ? cached : server;
}
function clearCachedWorkout(id: string) {
  const cached = readCachedWorkout();
  if (cached?.id === id) localStorage.removeItem(CACHE_KEY);
  clearWorkoutResumePosition(localStorage, id);
}
function splitList(value: string) { return value.split(",").map((item) => item.trim().toLowerCase().replaceAll(" ", "_")).filter(Boolean); }
function isTreadmill(exercise: Pick<Workout["exercises"][number], "name" | "equipment">) { return exercise.name === "Running (Treadmill)" || exercise.equipment.includes("treadmill"); }
function weightOptionsFor(exercise: Workout["exercises"][number], catalog: Exercise[]) {
  const match = catalog.find((item) => item.id === exercise.exerciseId || [item.name, ...item.aliases].some((name) => name.toLowerCase() === exercise.name.toLowerCase()));
  return match?.weightOptionsKg ?? [];
}
function syncLabel(state: "saved" | "saving" | "offline" | "unsaved") { return state === "saved" ? "Saved" : state === "saving" ? "Saving…" : state === "offline" ? "Saved offline" : "Unsaved changes"; }
function sourceLabel(workout: Workout) { return workout.source === "dot" ? "Dot" : workout.source === "codex" ? "Codex · Sol" : workout.source === "openai" ? "API · Terra" : workout.source === "strong_import" ? "Strong import" : workout.model ? "Manual + Terra" : "Manual"; }
function toLocalDateTime(iso: string) { const date = new Date(iso); const local = new Date(date.getTime() - date.getTimezoneOffset() * 60_000); return local.toISOString().slice(0, 16); }
